import moment from "moment";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";
import { getContractAsync } from "src/api/contract/contract-api";
import { useApiOperation } from "src/api/hooks";
import { getVisitorGroupInviteListAsync, getVisitorInviteGroupPageListAsync } from "src/api/visitor/visitor-api";
import type { VisitorGroupListItem, VisitorInviteGroupPageListItem, VisitorListItem } from "src/api/visitor/visitor-types";
import Header from "src/components/layout/Header";
import MetaTag from "src/components/layout/MetaTag";
import ExcelButton from "src/pages/mypage/contract/components/ExcelButton";
import HistoryDownloadFeedbackModals, { useHistoryDownloadFeedbackModals } from "src/pages/mypage/contract/components/HistoryDownloadFeedbackModals";
import HistoryPageControls from "src/pages/mypage/contract/components/HistoryPageControls";
import useExcelDownload from "src/pages/mypage/contract/hooks/useExcelDownload";
import { useNavigateGoBack } from "src/pages/hooks/navigate-go-back";
import {
  createVisitorGroupInviteListParams,
  createVisitorHistoryPageFromMeta,
  createVisitorInviteGroupPageListParams,
  hasVisitorGroupId,
  hasVisitorSearchContextTarget,
  isSelectVisitorHistoryDateRange,
  normalizeVisitorGroupInviteList,
} from "./functions/visitorHistory";
import VisitorHistoryList from "./components/VisitorHistoryList";
import { makeVisitorHistoryExcelData } from "./functions/makeExcelData";
import type { VisitorHistoryDateRange, VisitorHistoryPage, VisitorSearchContext } from "./types/visitorHistory";

const VISITOR_HISTORY_PAGE_SIZE = 20;
const VISITOR_HISTORY_DOWNLOAD_PAGE_SIZE = VISITOR_HISTORY_PAGE_SIZE;
const VISITOR_HISTORY_DETAIL_FETCH_BATCH_SIZE = VISITOR_HISTORY_PAGE_SIZE;
const INITIAL_VISITOR_HISTORY_DATE_RANGE: VisitorHistoryDateRange = [
  moment().subtract(7, "days").startOf("day").toDate(),
  moment().endOf("day").toDate(),
];
// 데이터 적을때 데이터 확보용 기간
// const VISITOR_HISTORY_DATE_PRESET_RANGES = [
//   { label: "1년", value: 12 },
//   { label: "2년", value: 24 },
//   { label: "4년", value: 48 },
// ];
const VISITOR_HISTORY_EMPTY_PAGE: VisitorHistoryPage = {
  currentPage: 0,
  totalPage: 0,
  totalCount: 0,
};

/**
 * 마이페이지 > 신청/계약 상세 > 방문자 초대내역
 */
const VisitorHistory = () => {
  const { t } = useTranslation(["visitor", "common"]);
  const { contractId } = useParams();
  const [searchParams] = useSearchParams();
  const { goBack } = useNavigateGoBack();
  const pageTitle = t("pageTitle");
  const contractManageId = searchParams.get("contractManageId") || undefined;
  const [dateRange, setDateRange] = useState<VisitorHistoryDateRange>(INITIAL_VISITOR_HISTORY_DATE_RANGE);
  const [isLoading, setIsLoading] = useState(false);
  const [isDownloadingExcel, setIsDownloadingExcel] = useState(false);
  const [isLoadingNextPage, setIsLoadingNextPage] = useState(false);
  const [visitorLogs, setVisitorLogs] = useState<VisitorListItem[]>();
  const [page, setPage] = useState<VisitorHistoryPage>(VISITOR_HISTORY_EMPTY_PAGE);
  const [searchContext, setSearchContext] = useState<VisitorSearchContext>();
  const isLoadingNextPageRef = useRef(false);

  const { executeAsync: getContract } = useApiOperation(getContractAsync);
  const { executeAsync: getVisitorInviteGroupPageList } = useApiOperation(getVisitorInviteGroupPageListAsync);
  const { executeAsync: getVisitorGroupInviteList } = useApiOperation(getVisitorGroupInviteListAsync);
  const { downloadExcel } = useExcelDownload();
  const {
    modal: downloadFeedbackModal,
    openContentEmptyAlert,
    closeContentEmptyAlert,
    openDownloadAlert,
    closeDownloadAlert,
    openErrorAlert,
    closeErrorAlert,
  } = useHistoryDownloadFeedbackModals();
  const hasNextPage = page.currentPage + 1 < page.totalPage;

  const fetchVisitorGroupDetails = useCallback(
    async (visitorGroups: VisitorInviteGroupPageListItem[]) => {
      const visitorGroupIds = visitorGroups.map((group) => group.contractVisitorGroupId).filter(hasVisitorGroupId);

      if (visitorGroupIds.length === 0) return visitorGroups as VisitorGroupListItem[];

      const visitorGroupDetails: VisitorGroupListItem[] = [];

      for (let index = 0; index < visitorGroupIds.length; index += VISITOR_HISTORY_DETAIL_FETCH_BATCH_SIZE) {
        const contractVisitorGroupIds = visitorGroupIds.slice(index, index + VISITOR_HISTORY_DETAIL_FETCH_BATCH_SIZE);
        const { data } = await getVisitorGroupInviteList(createVisitorGroupInviteListParams(contractVisitorGroupIds));
        const chunkDetails = data?.data?.contractVisitorGroup || [];

        visitorGroupDetails.push(...chunkDetails);
      }

      const visitorGroupDetailsById = new Map(
        visitorGroupDetails
          .filter((group) => hasVisitorGroupId(group.contractVisitorGroupId))
          .map((group) => [String(group.contractVisitorGroupId), group]),
      );

      return visitorGroups.map((group) => {
        const groupId = group.contractVisitorGroupId;

        return hasVisitorGroupId(groupId)
          ? visitorGroupDetailsById.get(String(groupId)) || (group as VisitorGroupListItem)
          : (group as VisitorGroupListItem);
      });
    },
    [getVisitorGroupInviteList],
  );

  const search = useCallback(
    async (context: VisitorSearchContext, targetDateRange: VisitorHistoryDateRange, options: { append?: boolean; page?: number } = {}) => {
      if (!isSelectVisitorHistoryDateRange(targetDateRange) || !hasVisitorSearchContextTarget(context)) {
        setVisitorLogs([]);
        setPage(VISITOR_HISTORY_EMPTY_PAGE);
        return;
      }

      const { data } = await getVisitorInviteGroupPageList(
        createVisitorInviteGroupPageListParams(context, targetDateRange, options.page || 0, VISITOR_HISTORY_PAGE_SIZE),
      );
      const contractVisitorPageItems = data?.data?.contractVisitorGroup || data?.data?.contractVisitor || [];
      const contractVisitorGroup = await fetchVisitorGroupDetails(contractVisitorPageItems);
      const contractVisitor = normalizeVisitorGroupInviteList(contractVisitorGroup);

      setVisitorLogs((prevVisitorLogs) => (options.append ? [...(prevVisitorLogs || []), ...contractVisitor] : contractVisitor));
      setPage(createVisitorHistoryPageFromMeta(data.meta.pageMeta, contractVisitorPageItems.length));
    },
    [fetchVisitorGroupDetails, getVisitorInviteGroupPageList],
  );

  const handleOnDateRangeChange = async (date: VisitorHistoryDateRange) => {
    if (isLoading) return;

    setDateRange(date);

    if (isSelectVisitorHistoryDateRange(date) === false) {
      setVisitorLogs([]);
      setPage(VISITOR_HISTORY_EMPTY_PAGE);
      return;
    }
  };

  const handleOnLoadNextPage = useCallback(async () => {
    if (isLoading || isLoadingNextPageRef.current || !hasNextPage || !isSelectVisitorHistoryDateRange(dateRange)) return;

    const nextPage = page.currentPage + 1;
    isLoadingNextPageRef.current = true;
    setIsLoadingNextPage(true);
    try {
      if (!searchContext) return;

      await search(searchContext, dateRange, { append: true, page: nextPage });
    } finally {
      isLoadingNextPageRef.current = false;
      setIsLoadingNextPage(false);
    }
  }, [dateRange, hasNextPage, isLoading, page.currentPage, search, searchContext]);

  useEffect(() => {
    (async () => {
      if (!contractId) {
        if (contractManageId) {
          setSearchContext({ contractManageId });
          return;
        }

        setVisitorLogs([]);
        setPage(VISITOR_HISTORY_EMPTY_PAGE);
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const { data } = await getContract({ contractId });
        const context = {
          contractManageId: data?.data?.contractManageId ?? contractManageId,
          contractName: data?.data?.contractName,
        };

        setSearchContext(context);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [contractId, contractManageId, getContract]);

  useEffect(() => {
    if (!searchContext) return;

    if (isSelectVisitorHistoryDateRange(dateRange) === false) {
      setVisitorLogs([]);
      setPage(VISITOR_HISTORY_EMPTY_PAGE);
      return;
    }

    (async () => {
      try {
        setIsLoading(true);
        await search(searchContext, dateRange);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [dateRange, search, searchContext]);

  const getVisitorHistoryExcelRows = async () => {
    if (!isSelectVisitorHistoryDateRange(dateRange)) {
      openErrorAlert();
      return null;
    }

    if (!searchContext) return null;

    const { data: firstData } = await getVisitorInviteGroupPageList(
      createVisitorInviteGroupPageListParams(searchContext, dateRange, 0, VISITOR_HISTORY_DOWNLOAD_PAGE_SIZE),
    );
    const contractVisitorPageItems = [...(firstData?.data?.contractVisitorGroup || firstData?.data?.contractVisitor || [])];
    const totalPages = firstData.meta.pageMeta?.totalPages;

    if (totalPages && totalPages > 1) {
      for (let i = 1; i < totalPages; i++) {
        const { data } = await getVisitorInviteGroupPageList(
          createVisitorInviteGroupPageListParams(searchContext, dateRange, i, VISITOR_HISTORY_DOWNLOAD_PAGE_SIZE),
        );
        contractVisitorPageItems.push(...(data?.data?.contractVisitorGroup || data?.data?.contractVisitor || []));
      }
    }

    const contractVisitorGroup = await fetchVisitorGroupDetails(contractVisitorPageItems);

    return normalizeVisitorGroupInviteList(contractVisitorGroup);
  };

  const handleDownloadExcel = async () => {
    try {
      setIsDownloadingExcel(true);
      const rows = await getVisitorHistoryExcelRows();

      if (!rows) return;
      if (rows.length === 0) {
        openContentEmptyAlert();
        return;
      }

      downloadExcel(makeVisitorHistoryExcelData(rows, searchContext?.contractName));

      if (!window.ReactNativeWebView) {
        openDownloadAlert();
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsDownloadingExcel(false);
    }
  };

  return (
    <>
      <MetaTag title={`${pageTitle} | TaapSpace`} />
      <Header //
        title={pageTitle}
        headerType="BACK"
        onClickBackButton={() => goBack()}
      />
      <HistoryPageControls //
        title={pageTitle}
        action={<ExcelButton title={t("downloadExcel")} onClick={handleDownloadExcel} disabled={isDownloadingExcel} />}
        disabled={isLoading}
        dateRange={dateRange}
        // 데이터 적을때 데이터 확보용 기간
        // datePresetRanges={VISITOR_HISTORY_DATE_PRESET_RANGES}
        onDateRangeChange={handleOnDateRangeChange}
      />
      <VisitorHistoryList //
        visitorLogs={visitorLogs}
        isLoading={isLoading}
        isLoadingNextPage={isLoadingNextPage}
        hasNextPage={hasNextPage}
        page={page}
        onLoadNextPage={handleOnLoadNextPage}
      />
      <HistoryDownloadFeedbackModals
        isDownloading={isDownloadingExcel}
        modal={downloadFeedbackModal}
        emptyMessage={
          <>
            <p>{t("emptyPeriod")}</p>
            <p>{t("emptyContent")}</p>
          </>
        }
        onCloseContentEmptyAlert={closeContentEmptyAlert}
        onCloseDownloadAlert={closeDownloadAlert}
        onCloseErrorAlert={closeErrorAlert}
      />
    </>
  );
};

export default VisitorHistory;
